/**
 * How densely a cartesian chart draws a large dataset: labels, point markers
 * and points in proportion to the pixels it has, so a chart of thousands of
 * categories draws in milliseconds instead of measuring every label.
 */

/** The width assumed for a chart not measured yet: a typical Report cell. */
export const UNMEASURED_WIDTH_PX = 600;

/** A chart's drawing width, a typical one until it is measured. */
export function plotWidth(widthPx: number): number {
  return widthPx > 0 ? widthPx : UNMEASURED_WIDTH_PX;
}

/** The most points a line draws a marker on; past it, markers are noise. */
export const MAX_POINT_MARKERS = 200;

export function showsPointMarkers(count: number): boolean {
  return count <= MAX_POINT_MARKERS;
}

function numeric(value: unknown): number {
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) ? number : 0;
}

/**
 * At most `budget` of a series' rows, in order, chosen by Largest-Triangle-
 * Three-Buckets over their positions and `valueKey`: the first and last rows
 * and, from each bucket between, the row that best keeps the line's shape,
 * so peaks and dips survive. A series within the budget is returned as is.
 */
export function downsampleSeries<Row extends Record<string, unknown>>(
  rows: readonly Row[],
  valueKey: string,
  budget: number,
): readonly Row[] {
  const count = rows.length;
  if (budget >= count || budget < 3) return rows;
  const value = (index: number) => numeric(rows[index]?.[valueKey]);
  const first = rows[0];
  const last = rows[count - 1];
  if (!first || !last) return rows;
  const sampled: Row[] = [first];
  const bucketSize = (count - 2) / (budget - 2);
  let previous = 0;
  for (let bucket = 0; bucket < budget - 2; bucket += 1) {
    const start = Math.floor(bucket * bucketSize) + 1;
    const end = Math.min(Math.floor((bucket + 1) * bucketSize) + 1, count - 1);
    // The next bucket's average point anchors the triangle's third corner.
    const nextStart = end;
    const nextEnd = Math.min(Math.floor((bucket + 2) * bucketSize) + 1, count);
    let averageX = 0;
    let averageY = 0;
    for (let index = nextStart; index < nextEnd; index += 1) {
      averageX += index;
      averageY += value(index);
    }
    const span = Math.max(1, nextEnd - nextStart);
    averageX /= span;
    averageY /= span;
    let best = start;
    let bestArea = -1;
    const previousY = value(previous);
    for (let index = start; index < end; index += 1) {
      const area = Math.abs(
        (previous - averageX) * (value(index) - previousY) -
          (previous - index) * (averageY - previousY),
      );
      if (area > bestArea) {
        bestArea = area;
        best = index;
      }
    }
    const chosen = rows[best];
    if (chosen) sampled.push(chosen);
    previous = best;
  }
  sampled.push(last);
  return sampled;
}

/** The most points a scatter chart draws. */
export const MAX_SCATTER_POINTS = 1000;
/** The size of the square a scatter chart draws one point per (about a marker's size). */
const SCATTER_CELL_PX = 4;

/**
 * A scatter chart's points as it can show them: one per few-pixel square of
 * the plot (a marker covers that much, so the rest draw on top of it), then
 * every nth past the cap. Outliers stay: an isolated point is alone in its
 * square.
 */
export function thinScatterPoints<Row extends Record<string, unknown>>(
  points: readonly Row[],
  xKey: string,
  yKey: string,
  size: { width: number; height: number },
): readonly Row[] {
  if (points.length <= MAX_SCATTER_POINTS) return points;
  const xs = points.map((point) => numeric(point[xKey]));
  const ys = points.map((point) => numeric(point[yKey]));
  // A loop, not Math.min(...xs): spreading many points overflows the stack.
  const extent = (values: number[]) =>
    values.reduce(
      ([low, high], value) => [Math.min(low, value), Math.max(high, value)],
      [Infinity, -Infinity],
    );
  const [minX = 0, maxX = 0] = extent(xs);
  const [minY = 0, maxY = 0] = extent(ys);
  const columns = Math.max(
    1,
    Math.floor(plotWidth(size.width) / SCATTER_CELL_PX),
  );
  const rows = Math.max(
    1,
    Math.floor((size.height > 0 ? size.height : 300) / SCATTER_CELL_PX),
  );
  const cell = (value: number, min: number, max: number, cells: number) =>
    max > min
      ? Math.min(cells - 1, Math.floor(((value - min) / (max - min)) * cells))
      : 0;
  const seen = new Set<number>();
  const kept: Row[] = [];
  points.forEach((point, index) => {
    const key =
      cell(ys[index] ?? 0, minY, maxY, rows) * columns +
      cell(xs[index] ?? 0, minX, maxX, columns);
    if (seen.has(key)) return;
    seen.add(key);
    kept.push(point);
  });
  if (kept.length <= MAX_SCATTER_POINTS) return kept;
  const step = kept.length / MAX_SCATTER_POINTS;
  return Array.from(
    { length: MAX_SCATTER_POINTS },
    (_, index) => kept[Math.floor(index * step)],
  ).filter((point): point is Row => point !== undefined);
}
